import { trackAnalyticsEvent } from '@autone/analytics';
import { useAutoneTranslation } from '@autone/translations';
import {
  ColumnVisibilityMenuContextProvider,
  ColumnVisibilityMenuOptionsPanel,
  ColumnVisibilityMenuReorderPanel,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Separator,
  TailwindButton,
} from '@autone/ui';
import { Columns3Icon } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { type ValueOf } from 'type-fest';

import { type APP_NAME } from '../../../consts';
import {
  type AssortmentCustomColumnsStorageKeys,
  type TableNamesWithCustomColumns,
  useAssortmentSessionStorageAtom,
} from '../../../storage/session-storage';

import {
  type AssortmentSessionStorageCustomColumnsDataMap,
  type PossibleAssortmentTableColumnIds,
} from './AssortmentCustomColumnsMenu.types';
import { getAssortmentCustomColumnsConfig } from './config';

interface AssortmentCustomColumnsMenuProps {
  tableName: TableNamesWithCustomColumns;
}

export const AssortmentCustomColumnsMenu = ({
  tableName,
}: AssortmentCustomColumnsMenuProps) => {
  const { t, common } = useAutoneTranslation<typeof APP_NAME>();
  const { storageKey, columnOptions, columnPinning, permanentColumns } =
    useMemo(
      () => getAssortmentCustomColumnsConfig(tableName, t),
      [tableName, t],
    );

  const [open, setOpen] = useState(false);
  const [customColumnAtom, setCustomColumnAtom] =
    useAssortmentSessionStorageAtom<AssortmentCustomColumnsStorageKeys>(
      storageKey,
    );
  const [localCustomColumns, setLocalCustomColumns] =
    useState<PossibleAssortmentTableColumnIds[]>(customColumnAtom);

  const handleCancelCustomColumns = useCallback(() => {
    setOpen(false);
  }, []);

  const handleConfirmCustomColumns = useCallback(() => {
    switch (tableName) {
      case 'productLocationsTable':
        trackAnalyticsEvent({
          category: 'assortment_tables',
          action: 'product_locations_table:custom_columns_updated',
        });
        break;
      case 'productsTable':
        trackAnalyticsEvent({
          category: 'assortment_tables',
          action: 'product_view_table:custom_columns_updated',
        });
        break;
      case 'skuLocationsTable':
        trackAnalyticsEvent({
          category: 'assortment_tables',
          action: 'sku_locations_table:custom_columns_updated',
        });
        break;
    }
    setOpen(false);
    setTimeout(() => {
      setCustomColumnAtom(
        localCustomColumns as ValueOf<AssortmentSessionStorageCustomColumnsDataMap>,
      );
    });
  }, [localCustomColumns, tableName, setCustomColumnAtom]);

  const handleTrackCustomColumnsClicked = useCallback(() => {
    switch (tableName) {
      case 'productLocationsTable':
        trackAnalyticsEvent({
          category: 'assortment_tables',
          action: 'product_locations_table:custom_columns_clicked',
        });
        break;
      case 'productsTable':
        trackAnalyticsEvent({
          category: 'assortment_tables',
          action: 'product_view_table:custom_columns_clicked',
        });
        break;
      case 'skuLocationsTable':
        trackAnalyticsEvent({
          category: 'assortment_tables',
          action: 'sku_locations_table:custom_columns_clicked',
        });
        break;
    }
  }, [tableName]);

  useEffect(() => {
    if (!open) {
      setLocalCustomColumns(customColumnAtom);
    }
  }, [customColumnAtom, open]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <TailwindButton
          onClick={handleTrackCustomColumnsClicked}
          variant={'ghost'}
          color={'secondary'}
          className="flex flex-row gap-2 py-1 px-2 justify-between self-stretch"
        >
          <p>{common('custom-columns.title')}</p>
          <Columns3Icon size={18} />
        </TailwindButton>
      </DialogTrigger>

      <DialogContent
        className={
          'p-0 gap-0 h-[75%] w-[60%] max-w-[800px] max-h-[800px] min-h-0 overflow-hidden'
        }
        showCloseButton={false}
      >
        <DialogHeader className="p-6">
          <DialogTitle>{common('custom-columns.title')}</DialogTitle>
        </DialogHeader>
        <Separator />
        <ColumnVisibilityMenuContextProvider
          columnOptions={columnOptions}
          columnPinning={columnPinning}
          permanentColumns={permanentColumns}
          columns={localCustomColumns}
          onColumnsChange={setLocalCustomColumns}
        >
          <div className="flex flex-row flex-1 min-h-0">
            <ColumnVisibilityMenuOptionsPanel className="p-6" />
            <Separator orientation="vertical" />
            <ColumnVisibilityMenuReorderPanel className="p-6" />
          </div>
        </ColumnVisibilityMenuContextProvider>
        <Separator />
        <DialogFooter className="p-6">
          <TailwindButton
            variant={'outlined'}
            color={'secondary'}
            onClick={handleCancelCustomColumns}
          >
            {common('buttons.cancel')}
          </TailwindButton>
          <TailwindButton
            variant={'contained'}
            onClick={handleConfirmCustomColumns}
          >
            {common('buttons.save-and-apply')}
          </TailwindButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
